import { useMemo } from "react";

import {
  ArrowPathIcon,
  BoltIcon,
  CheckBadgeIcon,
  CloudIcon,
  GlobeEuropeAfricaIcon,
  ScaleIcon,
  SparklesIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import Card from "../../shared/Card/Card";
import classNames from "../../../utils/classNames";
import { IPart } from "../../../models/Part";

/** Tiny deterministic 0..1 PRNG seeded from a string (FNV-1a + mulberry32). */
function seededRand(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const between = (rand: () => number, min: number, max: number) =>
  Math.round(min + rand() * (max - min));

const betweenF = (
  rand: () => number,
  min: number,
  max: number,
  decimals = 1
) => {
  const v = min + rand() * (max - min);
  const m = Math.pow(10, decimals);
  return Math.round(v * m) / m;
};

/**
 * Deduces a plausible part weight (kg) when the stored value is missing.
 * Uses the part title as a weak signal so brake discs don't weigh the same
 * as a seat; everything is still jittered per-part so the numbers feel
 * specific rather than templated.
 */
const estimatePartWeight = (part: IPart, rand: () => number): number => {
  if (typeof part.weight === "number" && part.weight > 0) return part.weight;
  const title = (part.title ?? part.category?.name ?? "").toLowerCase();
  const categoryBands: { match: string[]; min: number; max: number }[] = [
    { match: ["engine", "block"], min: 90, max: 180 },
    { match: ["gearbox", "transmission", "diff"], min: 35, max: 75 },
    { match: ["door"], min: 18, max: 32 },
    { match: ["bumper"], min: 6, max: 14 },
    { match: ["bonnet", "hood", "tailgate", "boot"], min: 14, max: 28 },
    { match: ["wing", "fender", "panel"], min: 6, max: 14 },
    { match: ["seat"], min: 12, max: 28 },
    { match: ["wheel", "alloy", "rim"], min: 8, max: 16 },
    { match: ["disc", "caliper", "brake"], min: 3, max: 9 },
    { match: ["ecu", "module", "sensor", "switch"], min: 0.2, max: 1.5 },
    { match: ["light", "lamp", "headlight", "tail"], min: 1, max: 4 },
    { match: ["mirror"], min: 0.8, max: 2.5 },
    { match: ["radiator", "condenser", "intercooler"], min: 4, max: 10 },
  ];
  const band = categoryBands.find((b) =>
    b.match.some((m) => title.includes(m))
  );
  if (band) return betweenF(rand, band.min, band.max, 1);
  return betweenF(rand, 2, 18, 1);
};

interface IPartSustainability {
  weightKg: number;
  /** CO2e avoided vs manufacturing a comparable new part (kg). */
  co2SavedKg: number;
  /** Energy saved through reuse rather than remanufacture (kWh). */
  energySavedKwh: number;
  /** Water saved (L) — casting/forming new parts is water-intensive. */
  waterSavedLitres: number;
  /** Landfill mass avoided by reselling this part (kg). */
  landfillAvoidedKg: number;
  /** Estimated remaining service life if reinstalled (years). */
  estimatedServiceLifeYears: number;
  /** Circularity 0..100 — loose composite across reuse potential signals. */
  circularityScore: number;
  /** Share of the item by mass that's recyclable at end-of-second-life. */
  recyclabilityRate: number;
}

const buildMockPartSustainability = (part: IPart): IPartSustainability => {
  const seed = part.id || part.tagNumber || "demo-part";
  const rand = seededRand(seed);

  const weightKg = estimatePartWeight(part, rand);

  // Manufacturing CO2 for vehicle components typically runs 5–9 kg CO2e/kg of
  // part mass, driven by material (aluminium > steel > plastic). Use a broad
  // band with a floor so small parts still register a meaningful impact.
  const co2PerKg = betweenF(rand, 4.5, 8.5, 2);
  const co2SavedKg = Math.max(0.4, Math.round(weightKg * co2PerKg * 10) / 10);

  const energySavedKwh = Math.round(weightKg * betweenF(rand, 6, 14, 2));
  const waterSavedLitres = Math.round(weightKg * between(rand, 18, 42));
  const landfillAvoidedKg = Math.round(weightKg * 10) / 10;
  const estimatedServiceLifeYears = betweenF(rand, 3, 9, 1);
  const recyclabilityRate = betweenF(rand, 0.75, 0.95, 3);

  // Circularity blends recyclability, remaining service life and the part's
  // "easy to reuse" signal (bigger structural parts score higher than
  // single-use consumables). Clamp to 40..96.
  const sizeBoost = Math.min(20, Math.round(weightKg / 4));
  const circularityScore = Math.max(
    40,
    Math.min(
      96,
      Math.round(
        recyclabilityRate * 55 +
          estimatedServiceLifeYears * 3 +
          sizeBoost +
          between(rand, -4, 6)
      )
    )
  );

  return {
    weightKg,
    co2SavedKg,
    energySavedKwh,
    waterSavedLitres,
    landfillAvoidedKg,
    estimatedServiceLifeYears,
    circularityScore,
    recyclabilityRate,
  };
};

const formatKg = (n: number) =>
  n >= 1000
    ? `${(n / 1000).toLocaleString("en-GB", { maximumFractionDigits: 2 })} t`
    : `${n.toLocaleString("en-GB", { maximumFractionDigits: 1 })} kg`;

const formatInt = (n: number) => n.toLocaleString("en-GB");

const scoreTone = (n: number) => {
  if (n >= 80) return "text-emerald-700";
  if (n >= 60) return "text-sky-700";
  if (n >= 40) return "text-amber-700";
  return "text-red-700";
};

const scoreRing = (n: number) => {
  if (n >= 80) return "bg-emerald-50 ring-emerald-100";
  if (n >= 60) return "bg-sky-50 ring-sky-100";
  if (n >= 40) return "bg-amber-50 ring-amber-100";
  return "bg-red-50 ring-red-100";
};

export default function StockSustainability({ part }: { part: IPart }) {
  const data = useMemo(() => buildMockPartSustainability(part), [part]);

  // Relatable comparisons so the headline number has context.
  const kmEquivalent = Math.round(data.co2SavedKg * 6.1);
  const phoneCharges = Math.round(data.energySavedKwh / 0.012); // ~12 Wh per full charge
  const showerMinutes = Math.round(data.waterSavedLitres / 9); // ~9 L/min

  return (
    <div className="flex flex-col gap-4">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 p-5">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br from-emerald-200/50 to-sky-200/40 blur-2xl"
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 via-teal-500 to-sky-500 text-white shadow-sm">
              <GlobeEuropeAfricaIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold text-gray-900">
                  Part Sustainability
                </h2>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-inset ring-amber-200">
                  Demo data
                </span>
              </div>
              <p className="max-w-xl text-xs text-gray-600">
                Environmental impact of reusing this part rather than
                manufacturing a new replacement.
              </p>
            </div>
          </div>

          {/* Circularity score dial */}
          <div className="flex items-center gap-3">
            <div
              className={classNames(
                "flex h-16 w-16 flex-none items-center justify-center rounded-full ring-4",
                scoreRing(data.circularityScore)
              )}
            >
              <span
                className={classNames(
                  "text-xl font-bold",
                  scoreTone(data.circularityScore)
                )}
              >
                {data.circularityScore}
              </span>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                Circularity score
              </p>
              <p className="text-sm font-semibold text-gray-800">
                {data.circularityScore >= 80
                  ? "High-value reuse"
                  : data.circularityScore >= 60
                    ? "Good reuse potential"
                    : data.circularityScore >= 40
                      ? "Moderate"
                      : "Low reuse value"}
              </p>
              <p className="text-[11px] text-gray-500">
                Composite of reusability, remaining life & recyclability.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Headline stats row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <HeadlineStat
          icon={CloudIcon}
          label="CO₂ avoided"
          value={formatKg(data.co2SavedKg)}
          hint={`≈ ${formatInt(kmEquivalent)} km of driving`}
          tone="emerald"
        />
        <HeadlineStat
          icon={BoltIcon}
          label="Energy saved"
          value={`${formatInt(data.energySavedKwh)} kWh`}
          hint={`≈ ${formatInt(phoneCharges)} phone charges`}
          tone="amber"
        />
        <HeadlineStat
          icon={TruckIcon}
          label="Water saved"
          value={`${formatInt(data.waterSavedLitres)} L`}
          hint={`≈ ${formatInt(showerMinutes)} min of showering`}
          tone="sky"
        />
        <HeadlineStat
          icon={ScaleIcon}
          label="Landfill avoided"
          value={formatKg(data.landfillAvoidedKg)}
          hint="This part's entire mass reused"
          tone="teal"
        />
      </div>

      {/* Lifecycle extension */}
      <Card title="Lifecycle extension" bodyClassName="p-4">
        <p className="text-xs text-gray-500 mb-3">
          Reusing this part extends its service life before re-entering the
          material recovery loop. Longer extension means more embodied carbon
          kept in circulation.
        </p>
        <div className="flex items-end gap-3">
          <div>
            <p className="text-3xl font-bold text-emerald-700">
              {data.estimatedServiceLifeYears}
              <span className="ml-1 text-base font-medium text-gray-500">
                yrs
              </span>
            </p>
            <p className="text-xs text-gray-500">estimated remaining service</p>
          </div>
          <div className="flex-1">
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-teal-400 to-sky-400"
                style={{
                  width: `${Math.min(100, (data.estimatedServiceLifeYears / 10) * 100)}%`,
                }}
              />
            </div>
            <div className="mt-1 flex justify-between text-[10px] font-medium uppercase tracking-wide text-gray-400">
              <span>0 yrs</span>
              <span>10 yrs</span>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3 text-xs">
          <LifecycleTile
            icon={ArrowPathIcon}
            title="Second life"
            description="Re-enters service on another vehicle without new tooling, casting or shipping from overseas factories."
          />
          <LifecycleTile
            icon={CheckBadgeIcon}
            title="Recyclable at end"
            description={`${Math.round(
              data.recyclabilityRate * 100
            )}% of this part by mass can be recovered when it eventually fails.`}
          />
          <LifecycleTile
            icon={TruckIcon}
            title="Local supply"
            description="Sourced from a UK dismantler, trimming long-haul freight emissions versus a factory-new import."
          />
        </div>
      </Card>

      {/* Narrative panel */}
      <Card bodyClassName="p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100">
            <SparklesIcon className="h-4 w-4" aria-hidden="true" />
          </div>
          <p className="text-sm leading-relaxed text-gray-700">
            Selling this{" "}
            <span className="font-semibold text-gray-900">
              {part.title ?? part.category?.name ?? "part"}
            </span>{" "}
            as a reuse keeps{" "}
            <span className="font-semibold text-emerald-700">
              {formatKg(data.weightKg)}
            </span>{" "}
            out of landfill and avoids roughly{" "}
            <span className="font-semibold text-emerald-700">
              {formatKg(data.co2SavedKg)} of CO₂e
            </span>{" "}
            compared with shipping and fitting a new one — equivalent to{" "}
            <span className="font-semibold">
              {formatInt(kmEquivalent)} km
            </span>{" "}
            of average-car driving.
          </p>
        </div>
      </Card>
    </div>
  );
}

function HeadlineStat({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  value: string;
  hint?: string;
  tone: "emerald" | "sky" | "amber" | "teal";
}) {
  const toneMap = {
    emerald: {
      ring: "ring-emerald-200",
      bg: "bg-emerald-50",
      icon: "text-emerald-600",
      value: "text-emerald-700",
    },
    sky: {
      ring: "ring-sky-200",
      bg: "bg-sky-50",
      icon: "text-sky-600",
      value: "text-sky-700",
    },
    amber: {
      ring: "ring-amber-200",
      bg: "bg-amber-50",
      icon: "text-amber-600",
      value: "text-amber-700",
    },
    teal: {
      ring: "ring-teal-200",
      bg: "bg-teal-50",
      icon: "text-teal-600",
      value: "text-teal-700",
    },
  }[tone];
  return (
    <div
      className={classNames(
        "flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 ring-1 ring-inset",
        toneMap.ring
      )}
    >
      <div
        className={classNames(
          "flex h-9 w-9 flex-none items-center justify-center rounded-md",
          toneMap.bg,
          toneMap.icon
        )}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </p>
        <p
          className={classNames(
            "truncate text-lg font-bold leading-tight",
            toneMap.value
          )}
        >
          {value}
        </p>
        {hint && (
          <p className="mt-0.5 truncate text-[11px] text-gray-500">{hint}</p>
        )}
      </div>
    </div>
  );
}

function LifecycleTile({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-md bg-gray-50/60 px-3 py-2 ring-1 ring-inset ring-gray-200">
      <div className="flex items-center gap-2 text-emerald-700">
        <Icon className="h-4 w-4" aria-hidden="true" />
        <span className="text-[11px] font-semibold uppercase tracking-wide">
          {title}
        </span>
      </div>
      <p className="mt-1 text-[11px] leading-snug text-gray-600">
        {description}
      </p>
    </div>
  );
}
